// src/Components/Streak/streakModel.ts
// Pure streak helpers shared by the Streak page, the celebration, the HUD chip
// and the nav badge: API types, flame tiers, countdown text, celebration copy
// and the month grid. No React, no network, unit tested.

export type DayStatus = "done" | "frozen" | "missed" | "pending" | "none";

export interface WeekDay {
  day: string; // local "YYYY-MM-DD"
  weekday: string; // "Mon"
  status: DayStatus;
  isToday: boolean;
}

export interface Reward {
  stars: number;
  coins: number;
  moonrocks: number;
}

export interface Milestone {
  days: number;
  reward: Reward;
  reached: boolean;
  claimed: boolean;
}

export interface NextMilestone {
  days: number;
  reward: Reward;
  remaining: number;
}

/** What every qualifying activity returns as `streak` (server extendStreak). */
export interface StreakResult {
  extendedToday: boolean;
  count: number;
  previousCount: number;
  longest: number;
  isNewRecord: boolean;
  totalDays: number;
  usedFreezes: number;
  restartedFrom: number;
  earnedFreeze: boolean;
  freezes: number;
  maxFreezes: number;
  milestoneReached: { days: number; reward: Reward } | null;
  nextMilestone: NextMilestone | null;
  todayKey: string;
  msUntilDayEnd: number;
  weekStrip: WeekDay[];
  streakDays?: string[];
  lastUpdated?: string | null;
}

/** GET /api/streak. */
export interface StreakSummary {
  count: number;
  longest: number;
  totalDays: number;
  extendedToday: boolean;
  todayKey: string;
  timezone: string;
  msUntilDayEnd: number;
  freezes: number;
  maxFreezes: number;
  freezeEvery: number;
  history: { day: string; status: "done" | "frozen" | "missed" }[];
  weekStrip: WeekDay[];
  milestones: Milestone[];
  claimable: number[];
  nextMilestone: NextMilestone | null;
  lastUpdated: string | null;
  streakDays: string[];
}

/* --------------------------------- tiers --------------------------------- */

export type TierKey = "unlit" | "ember" | "blaze" | "inferno" | "cosmic";

export interface Tier {
  key: TierKey;
  name: string;
  min: number;
  /** Inclusive upper bound; Infinity for the last tier. */
  max: number;
  /** Base flame size; it grows a little more within the tier. */
  scale: number;
}

export const TIERS: Tier[] = [
  { key: "unlit", name: "Unlit", min: 0, max: 0, scale: 0.7 },
  { key: "ember", name: "Ember", min: 1, max: 6, scale: 0.8 },
  { key: "blaze", name: "Blaze", min: 7, max: 29, scale: 0.92 },
  { key: "inferno", name: "Inferno", min: 30, max: 99, scale: 1.04 },
  { key: "cosmic", name: "Cosmic", min: 100, max: Infinity, scale: 1.14 },
];

export function tierFor(count: number): Tier {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  return TIERS.find((t) => n >= t.min && n <= t.max) || TIERS[0];
}

export function nextTierFor(count: number): Tier | null {
  const t = tierFor(count);
  const i = TIERS.indexOf(t);
  return TIERS[i + 1] || null;
}

/** True when `count` is the first day of a new tier (7, 30, 100). */
export function isTierUp(count: number): boolean {
  const t = tierFor(count);
  return t.key !== "unlit" && t.key !== "ember" && count === t.min;
}

/** Flame scale: the tier's base plus up to 0.08 as the count climbs through the tier. */
export function flameScale(count: number): number {
  const t = tierFor(count);
  if (!Number.isFinite(t.max)) return t.scale + Math.min(0.08, ((count - t.min) / 265) * 0.08);
  const span = Math.max(1, t.max - t.min);
  return +(t.scale + ((count - t.min) / span) * 0.08).toFixed(3);
}

/* ------------------------------- countdown ------------------------------- */

/** "3h 12m", "12m", "45s". Never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export function countdownLabel(ms: number): string {
  return `${formatCountdown(ms)} left today`;
}

/* ------------------------------- copy ------------------------------------ */

/** The one line under the number in the celebration. */
export function celebrationLine(r: Pick<StreakResult, "count" | "isNewRecord" | "usedFreezes" | "restartedFrom">): string {
  const n = r.count;
  if (n === 1 && r.restartedFrom > 0) return "A fresh start. Day 1!";
  if (n === 1) return "Day 1! Your flame is lit";
  if (r.isNewRecord) return `Day ${n}! Your longest yet`;
  if (r.usedFreezes > 0) return `Day ${n}! A freeze kept your flame alive`;
  return `Day ${n}! Keep it glowing`;
}

/** Days until the next freeze is earned (every `every` days of streak). */
export function daysToNextFreeze(count: number, every = 7): number {
  const r = count % every;
  return r === 0 ? every : every - r;
}

/** Progress (0..1) from the previous milestone (or 0) to the next one. */
export function milestoneProgress(count: number, milestones: number[]): { from: number; to: number | null; pct: number } {
  const sorted = [...milestones].sort((a, b) => a - b);
  const to = sorted.find((d) => d > count) ?? null;
  const from = [...sorted].reverse().find((d) => d <= count) ?? 0;
  if (to === null) return { from, to, pct: 1 };
  return { from, to, pct: Math.max(0, Math.min(1, (count - from) / (to - from))) };
}

/* ------------------------------- calendar -------------------------------- */

const pad = (n: number) => String(n).padStart(2, "0");

export function keyOf(year: number, month0: number, day: number): string {
  return `${year}-${pad(month0 + 1)}-${pad(day)}`;
}

export function parseKey(key: string): { year: number; month0: number; day: number } {
  const [y, m, d] = key.split("-").map(Number);
  return { year: y, month0: m - 1, day: d };
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface MonthCell {
  key: string | null;
  day: number | null;
}

/** Sunday-first weeks for a month; leading and trailing blanks are null cells. */
export function monthCells(year: number, month0: number): MonthCell[][] {
  const first = new Date(Date.UTC(year, month0, 1)).getUTCDay();
  const days = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const cells: MonthCell[] = [];
  for (let i = 0; i < first; i++) cells.push({ key: null, day: null });
  for (let d = 1; d <= days; d++) cells.push({ key: keyOf(year, month0, d), day: d });
  while (cells.length % 7) cells.push({ key: null, day: null });
  const weeks: MonthCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export function addMonths(year: number, month0: number, delta: number): { year: number; month0: number } {
  const t = year * 12 + month0 + delta;
  return { year: Math.floor(t / 12), month0: ((t % 12) + 12) % 12 };
}

/** Whether a streak day links to its neighbour: "start" | "mid" | "end" | "solo" | null. */
export function runShape(prevLit: boolean, lit: boolean, nextLit: boolean): "start" | "mid" | "end" | "solo" | null {
  if (!lit) return null;
  if (prevLit && nextLit) return "mid";
  if (prevLit) return "end";
  if (nextLit) return "start";
  return "solo";
}

/* ------------------------------- timezone -------------------------------- */

export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
